import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import lodashGet from 'lodash/get';
import { toast } from 'react-toastify';

import { useAppSelector } from '../../../../app/hooks';
import { DOWNLOAD } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../utils/mixpanel';
import { selectColumnCustomization, selectSelectedColumns } from '../../../../store/investor/selectors';
import { DataTableColumn, DataTableColumnType } from '../DataTable.types';
import { buildExportGrid, exportRowsToExcel } from './dataTableExportUtils';

export const EXPORT_EMPTY_VIEW_MESSAGE = 'There is no data to export for the current view';
export const EXPORT_FAILED_MESSAGE = 'Failed to export data. Please try again';

export const buildExportTruncatedMessage = (rowCount: number) =>
    `Export includes the first ${rowCount} rows only — narrow your filters to export the full set`;

export type FetchAllRowsResult = {
    rows: any[];
    /** True when the single bulk-export response hit the backend's 20,000-row server cap — the export may be incomplete. */
    truncated: boolean;
};

export type UseDataTableExportParams = {
    /** Mixpanel `Exports` property value — the page this export is triggered from. */
    source: 'Deals' | 'Leads';
    /** Same sourceKey the page's DataTable uses for column customization (e.g. 'DEALS' / 'LEADS_PAGE'). */
    filterSourceKey: string;
    /** Master column config for the currently active view (already picks Deal View vs Mandate View, etc.). */
    masterColumnConfig: DataTableColumn[];
    /**
     * Optional per-field export value-getters for columns the generic path can't resolve —
     * chiefly COMPONENT-typed columns (normally excluded) that DO have an underlying scalar,
     * e.g. Deals' "Probability" (rendered as a bar on screen, exported as its raw
     * customFields.debtOptionProbability text). A field present here is both included in the
     * sheet and resolved via this getter. Merged over the auto-derived owner getters.
     */
    columnValueGetters?: Record<string, (row: any) => string | number>;
    /** Fetches every row for the current view (all filters/search/sort/category applied, not just the current page). */
    fetchAllRows: () => Promise<FetchAllRowsResult>;
    getFileName: () => string;
    sheetName?: string;
};

export const useDataTableExport = ({
    source,
    filterSourceKey,
    masterColumnConfig,
    columnValueGetters,
    fetchAllRows,
    getFileName,
    sheetName = 'Sheet1',
}: UseDataTableExportParams) => {
    const [isExporting, setIsExporting] = useState(false);
    // Synchronous re-entrancy latch: `isExporting` state only lands after a
    // setState -> registry notify -> useSyncExternalStore -> effect ->
    // re-render round trip, leaving a window where ProfileButton's `disabled`
    // guard hasn't updated yet. A second click in that window must be blocked
    // synchronously, inside this hook, rather than relying solely on that
    // async-propagated caller-side guard.
    const isExportingRef = useRef(false);
    // Tracks whether the page that owns this export is still mounted, so an
    // export abandoned mid-flight (e.g. user navigates away) doesn't fire a
    // download, toast, Mixpanel event, or setState on an unmounted component.
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );
    const userData = useAppSelector((state) => state.user.userData);
    const ownerDropdownOptions = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const columnCustomization = useAppSelector(selectColumnCustomization);
    const selectedColumns = useAppSelector(selectSelectedColumns);

    // On screen, OWNER columns (OwnerCellRenderer.tsx / DealsRow.tsx's
    // getOwnerDropdownOption) store a bare userId and resolve it to a display
    // name against `state.investor.ownerDropdownOptions` — the SAME source
    // used here. Without this, owner columns would export raw userIds instead
    // of names. Built generically from whichever
    // columns are typed OWNER in the active master config, so it works for
    // both Deals (owners.dealOwners/uwAnalysts/...) and Leads without hardcoding field names.
    const ownerValueGetterMap = useMemo(() => {
        const nameById: Record<string, string> = {};
        (ownerDropdownOptions || []).forEach((owner: any) => {
            if (owner?.userId && owner?.name) nameById[owner.userId] = owner.name;
        });

        const map: Record<string, (row: any) => any> = {};
        (Array.isArray(masterColumnConfig) ? masterColumnConfig : []).forEach((column) => {
            if (column.type !== DataTableColumnType.OWNER) return;
            map[column.field] = (row: any) => {
                const raw = lodashGet(row, column.field);
                return typeof raw === 'string' ? nameById[raw] || raw : raw;
            };
        });
        return map;
    }, [ownerDropdownOptions, masterColumnConfig]);

    const handleExport = useCallback(async () => {
        // Synchronous re-entrancy guard: a second call landing in the same tick
        // (before setIsExporting's effects have propagated anywhere) is a no-op.
        if (isExportingRef.current) return;
        isExportingRef.current = true;
        // Set synchronously, before any await, so a second click fired while the
        // fetch below is still in flight sees isExporting: true and is blocked by
        // ProfileButton's disabled/action guards — otherwise that window allows a
        // duplicate export + duplicate Mixpanel `download` event.
        setIsExporting(true);
        try {
            const { rows, truncated } = await fetchAllRows();
            // The page that started this export may have unmounted while
            // fetchAllRows was in flight — bail out before any user-visible
            // side effect (toast/download/Mixpanel) fires on the new page.
            if (!isMountedRef.current) return;

            if (!rows?.length) {
                toast.info(EXPORT_EMPTY_VIEW_MESSAGE);
                return;
            }

            // Only apply the saved column selection if it belongs to this table's sourceKey —
            // exactly how DataTable.tsx decides between customized columns and the master config.
            const idsForColumns = columnCustomization.sourceKey === filterSourceKey ? selectedColumns : [];
            const safeMasterColumnConfig = Array.isArray(masterColumnConfig) ? masterColumnConfig : [];
            // Caller-supplied getters win over the auto-derived owner getters for the same field.
            const valueGetterMap = { ...ownerValueGetterMap, ...(columnValueGetters || {}) };
            const { headers, data } = buildExportGrid(safeMasterColumnConfig, idsForColumns, rows, valueGetterMap);

            if (!headers.length) {
                toast.info(EXPORT_EMPTY_VIEW_MESSAGE);
                return;
            }

            const succeeded = await exportRowsToExcel({
                sheetName,
                headers,
                data,
                fileName: getFileName(),
            });

            if (!isMountedRef.current) return;
            if (!succeeded) return;

            if (truncated) {
                toast.info(buildExportTruncatedMessage(rows.length));
            }

            Mixpanel.track(DOWNLOAD, {
                // Event contract per the product analytics spec: Type = the page
                // exported (Leads/Deals); PNO Org ID = the acting PNO's own org id
                // (not a per-row/company id — this event is the PNO exporting its
                // own CRM data); User ID = who triggered it; Time Stamp = download time.
                Type: source,
                'PNO Org ID': userData?.investorOrgId,
                'User ID': userData?.userId,
                'Time Stamp': new Date().toISOString(),
                truncated,
            });
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error(`Error exporting ${source} data:`, error);
            toast.error(EXPORT_FAILED_MESSAGE);
        } finally {
            isExportingRef.current = false;
            if (isMountedRef.current) setIsExporting(false);
        }
    }, [
        fetchAllRows,
        columnCustomization.sourceKey,
        filterSourceKey,
        selectedColumns,
        masterColumnConfig,
        ownerValueGetterMap,
        columnValueGetters,
        sheetName,
        getFileName,
        source,
        userData,
    ]);

    return { handleExport, isExporting };
};
